import { inject, Injectable, signal } from '@angular/core';
import { SettingsApiService } from '../../modules/settings/services/settings-api.service';
import { AuthService } from '../../modules/auth/services/auth.service';
import { VendorDetails } from '../../modules/settings/models/vendor-details';

/**
 * Shared, session-scoped read-model of the logged-in vendor's profile.
 *
 * Kept deliberately small: it exists so the navbar (and anything else) can show
 * the vendor logo/shop name without each consumer re-hitting the API. It is NOT
 * a source of truth for editing — the settings page keeps owning that and calls
 * `set()` with the PATCH response after a save so this cache stays fresh.
 *
 * Exactly two writers: `load()` once after login, and `set()` on settings save.
 * `clear()` runs on logout so the next vendor doesn't inherit stale data.
 */
@Injectable({
  providedIn: 'root'
})
export class VendorProfileService {

  private settingsApi = inject(SettingsApiService);
  private auth = inject(AuthService);

  private readonly _vendor = signal<VendorDetails | null>(null);
  readonly vendor = this._vendor.asReadonly();

  constructor() {
    // Wipe this cache on any logout path so the next user can't inherit it.
    this.auth.onSessionReset(() => this.clear());
  }

  /** Fetch the vendor profile once and cache it. No-op if already cached or not a vendor. */
  load(): void {
    if (this._vendor()) return;
    if (this.auth.getTokenData()?.role !== 'vendor') return;

    this.settingsApi.getVendorDetails().subscribe({
      next: (vendor) => this._vendor.set(vendor),
      // Silent on failure — the navbar falls back to the JWT user name/avatar.
      error: () => {}
    });
  }

  /** Sync the cache from an authoritative payload (e.g. the settings PATCH response). */
  set(vendor: VendorDetails): void {
    this._vendor.set(vendor);
  }

  /** Drop the cache so a subsequent login re-fetches. */
  clear(): void {
    this._vendor.set(null);
  }
}
